import React from 'react'
import { motion } from 'framer-motion'
import {
  LayoutIcon,
  LayersIcon,
  BoxIcon,
  MessageSquareIcon,
  GitBranchIcon,
  NetworkIcon,
} from 'lucide-react'

type SizeExample = {
  size: 'S' | 'M' | 'L'
  name: string
  units: number
  colorClass: string
  bgClass: string
  borderClass: string
  description: string
  examples: Array<{ title: string; icon: React.ReactNode }>
}

const sizes: SizeExample[] = [
  {
    size: 'S',
    name: 'Small',
    units: 1,
    colorClass: 'text-emerald-500',
    bgClass: 'bg-emerald-50',
    borderClass: 'border-emerald-500',
    description: 'Focused, clearly scoped work',
    examples: [
      { title: 'Simple login page', icon: <LayoutIcon size={20} /> },
      { title: 'Discovery workshop', icon: <MessageSquareIcon size={20} /> },
      { title: 'Button component', icon: <BoxIcon size={20} /> },
    ],
  },
  {
    size: 'M',
    name: 'Medium',
    units: 2,
    colorClass: 'text-blue-500',
    bgClass: 'bg-blue-50',
    borderClass: 'border-blue-500',
    description: 'Multi-step or integrated work',
    examples: [
      { title: 'User profile with settings', icon: <GitBranchIcon size={20} /> },
      { title: 'Dashboard with 3 charts', icon: <LayersIcon size={20} /> },
      { title: 'Payment form flow', icon: <LayoutIcon size={20} /> },
    ],
  },
  {
    size: 'L',
    name: 'Large',
    units: 4,
    colorClass: 'text-violet-500',
    bgClass: 'bg-violet-50',
    borderClass: 'border-violet-500',
    description: 'Complex, cross-functional work',
    examples: [
      { title: 'Complete onboarding (5 steps)', icon: <BoxIcon size={20} /> },
      { title: 'Admin panel with CRUD', icon: <NetworkIcon size={20} /> },
      { title: 'Multi-step checkout flow', icon: <GitBranchIcon size={20} /> },
    ],
  },
]

export function TShirtSizeGuide() {
  return (
    <div className="w-full bg-gray-50 rounded-2xl p-8 md:p-12 border border-gray-200 mt-16">
      <div className="text-center mb-10">
        <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 font-['Poppins']">
          How we scope work together
        </h3>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto font-['Roboto']">
          We size each request as S, M, or L so we share a clear language for
          scope — and so you always know what fits within your monthly capacity.
          Sizes guide planning; they are never priced individually.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
        {sizes.map((size, index) => (
          <motion.div
            key={size.size}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.1 }}
            className={`bg-white rounded-xl p-6 border border-gray-200 border-t-4 ${size.borderClass} shadow-sm flex flex-col h-full`}
          >
            <div className="flex items-center gap-4 mb-4">
              <div
                className={`flex items-center justify-center font-bold font-['Poppins'] rounded-full border-4 ${size.borderClass} ${size.bgClass} ${size.colorClass}`}
                style={{
                  width: size.size === 'S' ? '48px' : size.size === 'M' ? '56px' : '64px',
                  height: size.size === 'S' ? '48px' : size.size === 'M' ? '56px' : '64px',
                  fontSize: size.size === 'S' ? '1.25rem' : size.size === 'M' ? '1.5rem' : '1.75rem',
                }}
              >
                {size.size}
              </div>
              <div>
                <h4 className="text-xl font-bold text-gray-900 font-['Poppins'] leading-tight">{size.name}</h4>
                <div className="flex gap-1 mt-1">
                  {Array.from({ length: size.units }).map((_, i) => (
                    <div key={i} className={`w-2 h-4 rounded-sm ${size.colorClass.replace('text-', 'bg-')}`} />
                  ))}
                </div>
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-6 font-['Roboto']">{size.description}</p>
            <div className="mt-auto">
              <h5 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 font-['Poppins']">Examples</h5>
              <div className="space-y-2">
                {size.examples.map((example, i) => (
                  <div key={i} className={`flex items-center gap-3 p-3 rounded-lg ${size.bgClass}`}>
                    <div className={size.colorClass}>{example.icon}</div>
                    <span className="text-sm font-medium text-gray-800 font-['Roboto']">{example.title}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm flex flex-wrap items-center justify-center gap-6 md:gap-8 mx-auto max-w-3xl"
      >
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full border-4 border-violet-500 bg-violet-50 text-violet-500 flex items-center justify-center font-bold text-xl font-['Poppins']">L</div>
          <span className="text-2xl font-bold text-gray-900">=</span>
        </div>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full border-4 border-blue-500 bg-blue-50 text-blue-500 flex items-center justify-center font-bold text-lg font-['Poppins']">M</div>
          <span className="text-gray-500 font-medium">&times;2</span>
          <span className="text-2xl font-bold text-gray-900">=</span>
        </div>
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full border-4 border-emerald-500 bg-emerald-50 text-emerald-500 flex items-center justify-center font-bold text-base font-['Poppins']">S</div>
          <span className="text-gray-500 font-medium">&times;4</span>
        </div>
      </motion.div>

      <p className="text-sm text-gray-500 text-center mt-6 italic font-['Roboto']">
        Sizes are agreed upfront. Priorities can be rebalanced each month within
        your retainer's capacity.
      </p>
    </div>
  )
}